import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import axios from '../../common/axios';
import type { Notification } from '../../@types/user';

// Simple UUID generator
function generateUUID(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}-${Math.random().toString(36).substring(2, 11)}`;
}

type UserState = {
  isLoading: boolean;
  error: boolean;
  notifications: Notification[] | null;
  notificationsTotalCount: number;
  notificationsUnreadCount: number;
};

const initialState: UserState = {
  isLoading: false,
  error: false,
  notifications: null,
  notificationsTotalCount: 0,
  notificationsUnreadCount: 0
};

const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },

    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    getNotificationsSuccess(state, action) {
      state.isLoading = false;
      state.notifications = action.payload.items;
      state.notificationsTotalCount = action.payload.totalCount;
      state.notificationsUnreadCount = action.payload.unreadCount;
    },

    markNotificationAsReadSuccess(state, action) {
      const guid = action.payload;
      if (state.notifications) {
        const notification = state.notifications.find((n) => n.guid === guid);
        if (notification && notification.isUnRead) {
          notification.isUnRead = false;
          state.notificationsUnreadCount = Math.max(0, state.notificationsUnreadCount - 1);
        }
      }
    },

    markAllNotificationsAsReadSuccess(state) {
      if (state.notifications) {
        state.notifications.forEach((n) => {
          n.isUnRead = false;
        });
        state.notificationsUnreadCount = 0;
      }
    },

    appendNotification(state, action) {
      if (state.notifications) {
        state.notifications = [action.payload, ...state.notifications];
      } else {
        state.notifications = [action.payload];
      }
      state.notificationsUnreadCount += 1;
      state.notificationsTotalCount += 1;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const { appendNotification } = slice.actions;

export function getNotifications(page = 1, pageSize = 50) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('notifications', { params: { page, pageSize } });
      dispatch(slice.actions.getNotificationsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function markNotificationAsRead(guid: string) {
  return async () => {
    try {
      await axios.post(`notifications/${guid}/read`);
      dispatch(slice.actions.markNotificationAsReadSuccess(guid));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function markAllNotificationsAsRead() {
  return async () => {
    try {
      await axios.post('notifications/read-all');
      dispatch(slice.actions.markAllNotificationsAsReadSuccess());
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function uploadImageUser(file: any) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      const formData = new FormData();

      const extension = file.name.split('.').pop();

      const fileName = `${generateUUID()}.${extension}`;

      formData.append('file', file, fileName);

      await axios.post('documents/users', formData, config);

      return fileName;
    } catch {
      dispatch(slice.actions.hasError(true));
    }
    return '';
  };
}
